/**
 * Single source of truth for human-readable labels of every code, status,
 * severity, and role used across the BDS Ticket Hub UI.
 *
 * Rules:
 *   - Always render `<code> — <name>` for codes the user might not memorize
 *     (categories, severities). Examples: "A1 — Refund (unshipped)".
 *   - Statuses, moods, communication methods are already self-explanatory
 *     so they render plain.
 *   - "Ross" view-type is shown as "Admin" everywhere in the UI; the
 *     underlying database/enum value stays "Ross" so existing role guards
 *     keep working without a migration.
 *
 * If the backend ever adds new categories, update CATEGORY_NAMES below.
 * Severity definitions come from the email-template prefix policy.
 */

import { CATEGORY_LABEL, Severity, ViewType } from '@/types/domain';

// ──────────────────────────────────────────────────────────────────────────
//  Categories — canonical map lives in types/domain.ts (CATEGORY_LABEL).
//  Re-exported here so consumers only have to import from one place.
// ──────────────────────────────────────────────────────────────────────────
export const CATEGORY_NAMES: Record<string, string> = CATEGORY_LABEL;

export const CATEGORY_FAMILY: Record<string, 'Problem' | 'Sales'> = {
  A1: 'Problem', A2: 'Problem', A3: 'Problem', A4: 'Problem',
  A5: 'Problem', A6: 'Problem', A7: 'Problem',
  // V4.9.8 — B7 renamed to B6 (B6 slot was freed in V4.9). Sales family:
  // B1, B2, B3, B4, B5, B6.
  B1: 'Sales', B2: 'Sales', B3: 'Sales', B4: 'Sales', B5: 'Sales',
  B6: 'Sales'
};

/** "A1 — Refund (unshipped)" — for use in dropdowns, badges, table cells. */
export function categoryLabel(code: string | null | undefined, fallback = '—'): string {
  if (!code) return fallback;
  const name = CATEGORY_NAMES[code];
  return name ? `${code} — ${name}` : code;
}

/** Just the human name without the code prefix — for compact spaces. */
export function categoryName(code: string | null | undefined): string | null {
  if (!code) return null;
  return CATEGORY_NAMES[code] ?? null;
}

export const ALL_CATEGORY_CODES: string[] = Object.keys(CATEGORY_NAMES);

// ──────────────────────────────────────────────────────────────────────────
//  Severity / priority
// ──────────────────────────────────────────────────────────────────────────
export const SEVERITY_NAMES: Record<string, string> = {
  P1: 'Critical',
  P2: 'High',
  P3: 'Medium',
  P4: 'Low'
};

/** "P1 — Critical" */
export function severityLabel(code: string | null | undefined, fallback = '—'): string {
  if (!code) return fallback;
  const name = SEVERITY_NAMES[code];
  return name ? `${code} — ${name}` : code;
}

export const ALL_SEVERITY_CODES: string[] = Object.values(Severity);

// ──────────────────────────────────────────────────────────────────────────
//  View type / role  (Ross is shown as Admin in the UI)
// ──────────────────────────────────────────────────────────────────────────
export const VIEW_TYPE_LABELS: Record<ViewType, string> = {
  [ViewType.Admin]: 'Admin',
  [ViewType.Specialist]: 'Specialist',
  [ViewType.Communicator]: 'Communicator',
  [ViewType.SalesLead]: 'Sales Lead',
  [ViewType.Agent]: 'Agent'
};

export function viewTypeLabel(value: ViewType | string | null | undefined): string {
  if (!value) return '—';
  return VIEW_TYPE_LABELS[value as ViewType] ?? String(value);
}

/** Used to populate <select> options for view type. Order matters: Admin first. */
export const VIEW_TYPE_OPTIONS: Array<{ value: ViewType; label: string }> = [
  { value: ViewType.Admin, label: 'Admin' },
  { value: ViewType.Specialist, label: 'Specialist' },
  { value: ViewType.Communicator, label: 'Communicator' },
  { value: ViewType.SalesLead, label: 'Sales Lead' },
  { value: ViewType.Agent, label: 'Agent' }
];

// ──────────────────────────────────────────────────────────────────────────
//  Stores
// ──────────────────────────────────────────────────────────────────────────
export const STORE_NAMES: Record<string, string> = {
  USA: 'Backdropsource USA',
  UK: 'Backdropsource UK',
  AUS: 'Backdropsource AU',
  CA: 'Backdropsource CA',
  NZ: 'Backdropsource NZ',
  FR: 'Backdropsource France',
  DE: 'Backdropsource Germany',
  ES: 'Backdropsource Espana',
  UAE: 'Backdropsource UAE',
  IN: 'Backdropsource India'
};

export function storeLabel(code: string | null | undefined, fallback = '—'): string {
  if (!code) return fallback;
  const name = STORE_NAMES[code];
  return name ? `${code} — ${name}` : code;
}

export const ALL_STORE_CODES: string[] = Object.keys(STORE_NAMES);
